/**
 * Decides between the app and the sign-in screen.
 *
 * Must NOT wrap `/auth/callback` — that route runs inside the OAuth popup, and
 * redirecting it to a sign-in screen breaks every connect at the final step. See
 * `routes.tsx`, where the callback sits outside this component.
 */

import type { ReactNode } from 'react'
import { useAuth } from '@/core/auth/auth.context'
import { LoginPage } from './LoginPage'
import { Spinner } from '@/shared/components'

export function AuthGuard({ children }: { children: ReactNode }) {
  const { user, loginRequired, initialising } = useAuth()

  if (initialising) {
    // Rendering the login form here would flash it at an already-signed-in user
    // every time they reload.
    return (
      <div className="login-shell">
        <div className="row" style={{ gap: 10, color: 'var(--text-muted)' }}>
          <Spinner />
          <span>Checking your session…</span>
        </div>
      </div>
    )
  }

  // A server with no accounts allows anonymous access, so there is nothing to
  // sign in to. The sidebar shows an "open access" warning in that state.
  if (!loginRequired) return <>{children}</>

  if (!user) return <LoginPage />

  return <>{children}</>
}
